import React, { ComponentType, forwardRef, Ref } from 'react';

import { useRestoration, RestorationState } from 'hooks/restoration';

export type { RestorationState };

export interface WithRestorationProps {
  setRestorationState: (state: RestorationState) => void;
  restorationState?: RestorationState|null;
}

interface HOCProps {
  shouldCleanUp?: boolean;
  forwardedRef?: Ref<unknown>;
}

const withRestoration = <P extends Record<string, unknown>>(
  getId: (props: P) => string | undefined,
) => (
  Component: ComponentType<P & WithRestorationProps>,
): ComponentType<Omit<P, keyof WithRestorationProps> & HOCProps> => {
  const HOC = ({
    shouldCleanUp = false,
    forwardedRef = null,
    ...rest
  }: HOCProps & Omit<P, keyof WithRestorationProps>) => {
    const restorationId = getId(rest as P);
    const { setRestorationState, restorationState } = useRestoration(
      restorationId,
      shouldCleanUp,
    );

    return (
      <Component
        {...(rest as P)}
        setRestorationState={setRestorationState}
        restorationState={restorationState}
        ref={forwardedRef}
      />
    );
  };

  // prettier-ignore
  HOC.displayName = `withRestoration(${
    Component.displayName || Component.name
  })`;

  const ForwardedComponent = forwardRef<unknown, Omit<P, keyof WithRestorationProps> & Omit<HOCProps, 'forwardedRef'>>(
    (props, ref) => <HOC {...props} forwardedRef={ref} />,
  );

  ForwardedComponent.displayName = HOC.displayName;

  return ForwardedComponent as ComponentType<Omit<P, keyof WithRestorationProps> & HOCProps>;
};

export default withRestoration;
